import {
  Box,
  Tooltip,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Heading,
  Icon,
  Text,
  IconButton,
  useDisclosure,
  Button,
} from '@chakra-ui/react';
import { BarsProgressDuotone } from './icons/bars-progress-duotone';
import { useDrafts } from 'hooks/useDrafts';
import { useFirebaseUser } from 'utils/useFirebaseUser';
import { useContext, useEffect, useMemo, useState } from 'react';
import { radius } from 'theme/names';
import { DynamicTable } from 'components/dynamic-table';
import { FaAngleDown, FaLinkedin } from 'react-icons/fa';
import { TweetContext } from 'context/tweetContext';
import { EditIcon } from 'components/queue-panels/icons/edit-icon';
import { EyeIcon } from 'components/queue-panels/icons/eye-icon';
import { EditPageIcon } from 'components/queue-panels/icons/edit-page-icon';
import { SettingsPopup } from 'components/queue-panels/settings-popup';
import { useAddToQueue } from 'hooks/useAddToQueue';
import Link from 'next/link';

export const DraftTable = () => {
  const tweetContext: any = useContext(TweetContext);
  const [opendDraft, setOpendDraft] = useState<any>(null);
  const {
    isOpen: isOpenSettings,
    onOpen: onOpenSettings,
    onClose: onCloseSettings,
  } = useDisclosure();

  const { drafts, hasMore, loadDrafts, loadMoreDrafts } = useDrafts(5);
  const handleAddToQueue = useAddToQueue();

  const isAuthenticated = useFirebaseUser();
  useEffect(() => {
    if (isAuthenticated) {
      loadDrafts();
    }
  }, [isAuthenticated]);

  const columns = useMemo(
    () => [
      {
        accessor: 'text',
        Header: 'Posts',
        width: '80%',
        Cell: ({ value, row }) => (
          <Flex alignItems="center">
            <Box
              {...{
                bg: 'background.lightMode.light',
                color: 'text.lightMode.standart',
                _dark: {
                  bg: 'background.darkMode.light',
                  color: 'text.darkMode.standart',
                },
                borderRadius: radius.sm,
                fontSize: '14px',
                placeContent: 'center',
                padding: 2,
                marginRight: 4,
                cursor: 'pointer',
              }}
              onClick={(e) => {
                e.stopPropagation();
                tweetContext.newTweet(row.original, row.original.id);
                tweetContext.setIsTweetTextChanged(true);
                tweetContext.open();
              }}
            >
              <Icon
                {...{
                  viewBox: '0 0 17 15',
                }}
              >
                <EditIcon />
              </Icon>
            </Box>
            <FaLinkedin color="#0A66C2" />
            <Text
              pl={4}
              minW="100%"
              maxW="320px"
              textAlign="start"
              noOfLines={1}
              wordBreak={'break-all'}
              isTruncated
            >
              {value}
            </Text>
          </Flex>
        ),
      },
      {
        accessor: 'actions',
        Header: (
          <Box display="flex" justifyContent="end">
            Actions
          </Box>
        ),
        width: '20%',
        Cell: ({ value, row }) => (
          <Box display="flex" justifyContent="end">
            <Tooltip hasArrow label="Preview">
              <IconButton
                aria-label="Preview"
                variant="action"
                size="table"
                mr={2}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpendDraft(row.original);
                  onOpenSettings();
                }}
                icon={
                  <Icon fontSize="22px">
                    <EyeIcon />
                  </Icon>
                }
              />
            </Tooltip>
            <Tooltip hasArrow label="Edit">
              <IconButton
                aria-label="Edit"
                variant="action"
                size="table"
                onClick={(e) => {
                  e.stopPropagation();
                  tweetContext.newTweet(row.original, row.original.id);
                  tweetContext.setIsTweetTextChanged(true);
                  tweetContext.open();
                }}
                icon={
                  <Icon fontSize="22px">
                    <EditPageIcon />
                  </Icon>
                }
              />
            </Tooltip>
          </Box>
        ),
      },
    ],
    [tweetContext, onOpenSettings, setOpendDraft]
  );

  return (
    <Card w="100%" flexShrink={0}>
      <CardHeader>
        <Heading size="sm">
          <Flex gap={2} alignItems="center">
            <BarsProgressDuotone />
            <Text>Your Drafts</Text>
          </Flex>
        </Heading>
      </CardHeader>
      <CardBody>
        <DynamicTable
          data={drafts}
          columns={columns}
          emptyMessage="No drafts available"
        />
        <Flex justifyContent="space-between" mt={4}>
          <Link href="/queue">
            <Button variant="secondary">Go to my queue</Button>
          </Link>
          {hasMore ? (
            <Button
              variant="secondary"
              rightIcon={<FaAngleDown />}
              onClick={() => {
                loadMoreDrafts();
              }}
            >
              Show more
            </Button>
          ) : null}
          <Box></Box>
        </Flex>
        <SettingsPopup
          isOpen={isOpenSettings}
          onClose={onCloseSettings}
          tweet={opendDraft || { text: '' }}
          hideAdvancedButton={false}
          showAddToQueue
          isGhostwriter={false}
          handleAddToQueue={handleAddToQueue(opendDraft)}
        />
      </CardBody>
    </Card>
  );
};
